import { useState } from 'react';
import classNames from 'classnames';

// hooks
import { useDateTime } from 'helpers/hooks/useDateTime';

// MUI components
import { makeStyles } from '@material-ui/core/styles';
import {
  Box,
  Typography,
  Tooltip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
} from '@material-ui/core';
import { Schedule, MoreVert, Edit, Delete } from '@material-ui/icons';

// utils
import {
  formatRelativeDateTime,
  isEditableScheduledMessage,
} from './ScheduleMessageDialog/utils';

type MessageProps = {
  message: TypedMap<Message>;
  onUpdate: (message: TypedMap<Message>) => void;
  onDelete: (message: TypedMap<Message>) => void;
};

const Message = function({ message, onUpdate, onDelete }: MessageProps) {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const createdOn = useDateTime(message.get('createdOn'));
  const timestampLabel = `${createdOn.date} at ${createdOn.time}`;
  const scheduledAt = message.get('scheduledAt');
  const isScheduled = message.get('status') === 'scheduled';
  const isEditable = isScheduled && isEditableScheduledMessage(scheduledAt);

  const openMenu = (event: React.MouseEvent<HTMLElement>) =>
    setAnchorEl(event.currentTarget);
  const closeMenu = () => setAnchorEl(null);
  const handleUpdate = () => {
    closeMenu();
    onUpdate(message);
  };
  const handleDelete = () => {
    closeMenu();
    onDelete(message);
  };

  return (
    <Box className={classes.messageRow}>
      {isEditable && (
        <>
          <IconButton
            id={`messageActionsButton-${message.get('id')}`}
            size='small'
            aria-label='Scheduled message actions'
            onClick={openMenu}
          >
            <MoreVert fontSize='small' />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={closeMenu}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem dense onClick={handleUpdate}>
              <ListItemIcon className={classes.menuIcon}>
                <Edit fontSize='small' />
              </ListItemIcon>
              Update
            </MenuItem>
            <MenuItem dense onClick={handleDelete}>
              <ListItemIcon className={classes.menuIcon}>
                <Delete fontSize='small' />
              </ListItemIcon>
              Delete
            </MenuItem>
          </Menu>
        </>
      )}
      <Tooltip
        title={isScheduled ? '' : timestampLabel}
        placement={message.get('type') === 'outgoing' ? 'left' : 'right'}
      >
        <Box
          className={classNames(
            classes.threadItemContainer,
            message.get('type') === 'outgoing'
              ? classes.outgoingThreadItem
              : classes.incomingThreadItem,
            isScheduled && classes.scheduledThreadItem
          )}
        >
          <Typography variant='body2' className={classes.messageBodyText}>
            {message.get('body')}
          </Typography>
          {isScheduled && scheduledAt && (
            <Box className={classes.scheduledFooter}>
              <Schedule className={classes.scheduledIcon} />
              <Typography variant='caption'>
                Sends {formatRelativeDateTime(scheduledAt)}
              </Typography>
            </Box>
          )}
        </Box>
      </Tooltip>
    </Box>
  );
};
const useStyles = makeStyles(theme => {
  return {
    messageRow: {
      display: 'flex',
      alignItems: 'center',
      gap: 4,
    },
    menuIcon: { minWidth: 32 },
    threadItemContainer: {
      borderRadius: 20,
      paddingLeft: 15,
      paddingRight: 15,
      paddingTop: 8,
      paddingBottom: 8,
    },
    outgoingThreadItem: {
      backgroundColor: theme.palette.primary.main,
    },
    incomingThreadItem: {
      backgroundColor: (theme.palette.background as any).lightPaper,
    },
    scheduledThreadItem: {
      opacity: 0.6,
      border: `1px dashed ${theme.palette.primary.contrastText}`,
    },
    messageBodyText: {
      whiteSpace: 'pre-line',
    },
    scheduledFooter: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      gap: 4,
      marginTop: 4,
    },
    scheduledIcon: { fontSize: 12 },
  };
});
export default Message;
